import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Cookie, X, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';

const COOKIE_CONSENT_KEY = 'motoreveal_cookie_consent';
const SHOW_COOKIE_BANNER_EVENT = 'motoreveal_show_cookie_banner';

interface CookiePreferences {
  essential: boolean; // Always true, cannot be disabled
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
}

const defaultPreferences: CookiePreferences = {
  essential: true,
  functional: true,
  analytics: true,
  marketing: false,
  timestamp: 0,
};

// Function to trigger showing the cookie banner
export const showCookieBanner = () => {
  window.dispatchEvent(new CustomEvent(SHOW_COOKIE_BANNER_EVENT));
};

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);

  // Load existing preferences
  const loadPreferences = useCallback(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setPreferences(parsed);
      } catch {
        setPreferences(defaultPreferences);
      }
    }
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!stored) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => setVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Listen for show banner events
  useEffect(() => {
    const handleShowBanner = () => {
      loadPreferences();
      setShowDetails(true); // Show details when reopening
      setVisible(true);
    };

    window.addEventListener(SHOW_COOKIE_BANNER_EVENT, handleShowBanner);
    return () => window.removeEventListener(SHOW_COOKIE_BANNER_EVENT, handleShowBanner);
  }, [loadPreferences]);

  const savePreferences = (prefs: CookiePreferences) => {
    const prefsWithTimestamp = { ...prefs, timestamp: Date.now() };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefsWithTimestamp));
    setVisible(false);
  };

  const handleAcceptAll = () => {
    savePreferences({
      essential: true,
      functional: true,
      analytics: true,
      marketing: true,
      timestamp: Date.now(),
    });
  };

  const handleRejectNonEssential = () => {
    savePreferences({
      essential: true,
      functional: false,
      analytics: false,
      marketing: false,
      timestamp: Date.now(),
    });
  };

  const handleSavePreferences = () => {
    savePreferences(preferences);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 animate-fade-in">
      <div className="max-w-4xl mx-auto bg-card border border-border rounded-xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className="p-4 md:p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Cookie className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-lg mb-1">Cookie Preferences</h3>
              <p className="text-sm text-muted-foreground">
                We use cookies to enhance your experience, analyze site traffic, and for marketing purposes. 
                You can customize your preferences or accept all cookies.{' '}
                <Link to="/cookie-policy" className="text-primary hover:underline">
                  Learn more about our cookie policy
                </Link>
              </p>
            </div>
            <button 
              onClick={handleRejectNonEssential}
              className="p-1 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Expandable Details */}
          <div className="mt-4">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center gap-2 text-sm font-medium text-primary hover:underline"
            >
              {showDetails ? (
                <>
                  <ChevronUp className="w-4 h-4" />
                  Hide cookie settings
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" />
                  Customize cookie settings
                </>
              )}
            </button>

            {showDetails && (
              <div className="mt-4 space-y-4 pt-4 border-t border-border">
                {/* Essential Cookies */}
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">Essential Cookies</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">Required</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Necessary for the website to function. Cannot be disabled.
                    </p>
                  </div>
                  <Switch checked={true} disabled />
                </div>

                {/* Functional Cookies */}
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <span className="font-medium text-sm">Functional Cookies</span>
                    <p className="text-xs text-muted-foreground mt-1">
                      Remember your preferences like theme and language settings.
                    </p>
                  </div>
                  <Switch
                    checked={preferences.functional}
                    onCheckedChange={(checked) => 
                      setPreferences(prev => ({ ...prev, functional: checked }))
                    }
                  />
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <span className="font-medium text-sm">Analytics Cookies</span>
                    <p className="text-xs text-muted-foreground mt-1">
                      Help us understand how visitors interact with our website.
                    </p>
                  </div>
                  <Switch
                    checked={preferences.analytics}
                    onCheckedChange={(checked) => 
                      setPreferences(prev => ({ ...prev, analytics: checked }))
                    }
                  />
                </div>

                {/* Marketing Cookies */}
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <span className="font-medium text-sm">Marketing Cookies</span>
                    <p className="text-xs text-muted-foreground mt-1">
                      Used to deliver relevant ads and track campaign effectiveness.
                    </p>
                  </div>
                  <Switch
                    checked={preferences.marketing}
                    onCheckedChange={(checked) => 
                      setPreferences(prev => ({ ...prev, marketing: checked }))
                    }
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className={cn(
          "px-4 md:px-6 py-4 bg-muted/50 border-t border-border",
          "flex flex-col sm:flex-row gap-3 sm:justify-end"
        )}>
          {showDetails ? (
            <>
              <Button
                variant="outline"
                onClick={handleRejectNonEssential}
                className="sm:order-1"
              >
                Reject All
              </Button>
              <Button
                onClick={handleSavePreferences}
                className="sm:order-2"
              >
                Save Preferences
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={handleRejectNonEssential}
                className="sm:order-1"
              >
                Reject Non-Essential
              </Button>
              <Button
                onClick={handleAcceptAll}
                className="sm:order-2"
              >
                Accept All Cookies
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Utility function to get current cookie preferences
export const getCookiePreferences = (): CookiePreferences | null => {
  const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
};

// Utility function to check if a specific cookie type is allowed
export const isCookieAllowed = (type: keyof Omit<CookiePreferences, 'timestamp'>): boolean => {
  const prefs = getCookiePreferences();
  if (!prefs) return false;
  return prefs[type] ?? false;
};
